import React from "react";
import { formatDate } from "../../../service/convert/date";
import { useNavigate } from "react-router-dom";
import { IDocument } from "../../../store/@types/document";
import { useAppDispatch, useAppSelector } from "../../../store";
import { Popconfirm } from "antd";
import { deleteDocument, getDocument } from "../../../service/api/document";

import {
  IconEye,
  IconEdit,
  IconTrash,
  IconFileTextFilled,
} from "@tabler/icons-react";

type IFileData = {
  item: IDocument;
};

const MobileFileCard: React.FC<IFileData> = ({ item }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(({ loading }) => loading.update);
  const user = useAppSelector(({ auth }) => auth.user);

  const canEdit = user?.role === "1";
  const canDelete = user?.role === "1";

  const handleEdit = () => {
    if (!canEdit) return;
    getDocument(dispatch, item?.id);
    navigate(`/dashboard/documents/${item?.id}/edit`);
  };

  const handleDelete = () => {
    if (!canDelete) return;
    deleteDocument(dispatch, item?.id, navigate);
  };

  const expired = item.expired === "1";

  return (
    <div
      className={`w-full bg-white rounded-xl border border-gray-100/20 hover:border-primary-100 hover:shadow-md transition-all duration-200 mb-3 overflow-hidden`}
    >
      {/* Top row: icon + title + badge */}
      <div className="flex items-center gap-3 px-3 pt-3 pb-2">
        <div
          className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center
            ${expired ? "bg-red-50/20" : "bg-primary-50/20"}`}
        >
          <IconFileTextFilled
            size={20}
            className={expired ? "text-red-500" : "text-primary-600"}
          />
        </div>

        <div className="flex-1 min-w-0">
          <p
            className={`text-sm font-medium truncate leading-tight
              ${expired ? "text-red-600" : "text-primary-600"}`}
          >
            {item?.name}
          </p>
          <span
            className={`inline-block mt-1 text-[10px] font-medium px-2 py-0.5 rounded-full
              ${
                expired
                  ? "bg-red-50/20 text-red-700"
                  : "bg-primary-50/20 text-primary-700"
              }`}
          >
            {expired ? "Expired" : "Active"}
          </span>
        </div>
      </div>

      {/* Meta rows */}
      <div className="px-3 pb-2 space-y-1">
        {item?.issuing_body && (
          <div className="flex justify-between items-center">
            <span className="text-[11px] text-gray-400">Issued</span>
            <span className="text-[11px] text-gray-500">
              {formatDate(item.issuing_date)}
            </span>
          </div>
        )}
        {item?.expiring_date && (
          <div className="flex justify-between items-center">
            <span className="text-[11px] text-gray-400">Expires</span>
            <span
              className={`text-[11px] ${expired ? "text-red-500 font-medium" : "text-gray-500"}`}
            >
              {formatDate(new Date(item.expiring_date))}
            </span>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="flex justify-end items-center gap-2 px-3 py-2 border-t border-gray-100/20">
        <button
          onClick={() => navigate("/dashboard/documents/" + item?.id)}
          className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-100/30
                     bg-gray-50/20 text-gray-400 hover:text-primary-600 hover:bg-gray-100
                     transition-colors duration-150"
          aria-label="View document"
        >
          <IconEye size={16} />
        </button>

        {canEdit && (
          <button
            onClick={handleEdit}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-100/30
                       bg-gray-50/20 text-gray-400 hover:text-primary-600 hover:bg-gray-100
                       transition-colors duration-150"
            aria-label="Edit document"
          >
            <IconEdit size={16} />
          </button>
        )}

        {canDelete && (
          <Popconfirm
            title="Delete Document"
            description="Are you sure you want to delete this document? This action cannot be undone."
            onConfirm={handleDelete}
            okButtonProps={{
              loading,
              className: "bg-red-500 hover:bg-red-600 text-white border-none",
            }}
            okText="Delete"
            cancelText="Cancel"
          >
            <button
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-red-100/30
                         bg-gray-50/20 text-red-400 hover:bg-red-50 transition-colors duration-150"
              aria-label="Delete document"
            >
              <IconTrash size={16} />
            </button>
          </Popconfirm>
        )}
      </div>
    </div>
  );
};

export default MobileFileCard;
